// src/pages/OpeningBalanceCheck.jsx
// Phase E2 — Opening Balance Reconciliation.
// Compares manually-entered prior year figures against the prior year's
// completed AFS job already on file for this client (if any), and surfaces
// differences with AI-suggested explanations.
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getClients, reconcileOpeningBalances, reconcileAction } from '../api/kailux'
import ErrorMessage from '../components/ErrorMessage'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const CATEGORIES = [
  'Property Plant & Equipment','Accumulated Depreciation','Intangible Assets','Investments',
  'Inventories','Trade and Other Receivables','Tax Receivables','Cash and Cash Equivalents',
  'Stated Capital','Retained Earnings','Loans from Shareholders',
  'Borrowings - Non-Current','Borrowings - Current','Deferred Tax Liability',
  'Trade and Other Payables','Deferred Revenue',
]

const inputStyle = {
  width:'100%', padding:'9px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px',
  textAlign:'right',
}

export default function OpeningBalanceCheck() {
  const { firm } = useAuth()
  const location = useLocation()

  const [clients, setClients]   = useState([])
  const [clientId, setClientId] = useState(location?.state?.client?.id || '')
  const [clientEmail, setClientEmail] = useState('')
  const [figures, setFigures]   = useState(location?.state?.priorFigures || {})
  const [checking, setChecking] = useState(false)
  const [result, setResult]     = useState(null)
  const [error, setError]       = useState('')
  const [actioned, setActioned] = useState({}) // {category: 'accepted'|'flagged'}

  useEffect(() => {
    if (!firm?.id) return
    getClients(firm.id).then(res => setClients(res.data || [])).catch(console.error)
  }, [firm?.id])

  const setFigure = (cat, val) => setFigures(f => ({ ...f, [cat]: val === '' ? '' : Number(val) }))

  const selectedClient = clients.find(c => c.id === clientId)

  const runCheck = async () => {
    if (!clientId) return setError('Please select a client')
    setError(''); setChecking(true); setResult(null); setActioned({})
    try {
      const numericFigures = {}
      Object.entries(figures).forEach(([k,v]) => { if (v !== '' && v != null) numericFigures[k] = Number(v) })
      const res = await reconcileOpeningBalances({
        client_id: clientId, firm_id: firm.id,
        company: selectedClient?.name || '', prior_figures: numericFigures,
      })
      setResult(res.data)
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not run the reconciliation check.')
    } finally {
      setChecking(false)
    }
  }

  const handleAction = async (item, action) => {
    try {
      await reconcileAction({
        client_id: clientId, firm_id: firm.id,
        client_name: selectedClient?.name || '', client_email: clientEmail,
        firm_name: firm?.name || '',
        category: item.category, difference: item.difference, note: item.ai_note,
        action,
      })
      setActioned(a => ({ ...a, [item.category]: action }))
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not record this action.')
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, padding:'32px' }}>

      <div style={{ marginBottom:'24px' }}>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800' }}>
          Opening Balance Reconciliation
        </div>
        <div style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginTop:'4px' }}>
          Compare this year's opening figures against last year's signed AFS — catch
          mismatches before they reach the financial statements
        </div>
      </div>

      <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'24px', marginBottom:'24px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'4px' }}>
          <div>
            <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Client</label>
            <select value={clientId} onChange={e => { setClientId(e.target.value); setResult(null) }}
              style={{ ...inputStyle, textAlign:'left' }}>
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>
              Client email (only needed if you flag a difference for follow-up)
            </label>
            <input value={clientEmail} onChange={e => setClientEmail(e.target.value)}
              placeholder="director@client.co.za" style={{ ...inputStyle, textAlign:'left' }} />
          </div>
        </div>
      </div>

      <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'24px', marginBottom:'24px' }}>
        <div style={{ fontSize:'14px', fontWeight:'700', marginBottom:'4px' }}>
          Enter this year's opening balances (= last year's closing balances, per your TB)
        </div>
        <div style={{ fontSize:'12px', color:T.mt, marginBottom:'16px' }}>
          Leave blank any category you don't have — only categories with a figure entered will be checked.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' }}>
          {CATEGORIES.map(cat => (
            <div key={cat}>
              <label style={{ fontSize:'12px', color:T.lt, display:'block', marginBottom:'4px' }}>{cat}</label>
              <input type="number" value={figures[cat] ?? ''} onChange={e => setFigure(cat, e.target.value)}
                placeholder="0.00" style={inputStyle} />
            </div>
          ))}
        </div>

        {error && <ErrorMessage style={{ marginTop:'16px' }}>{error}</ErrorMessage>}

        <button onClick={runCheck} disabled={checking}
          style={{ marginTop:'18px', padding:'12px 28px', borderRadius:'8px', border:'none',
            background:T.prg, color:T.onpri,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
            cursor: checking ? 'default' : 'pointer' }}>
          {checking ? 'Checking…' : 'Run reconciliation check →'}
        </button>
      </div>

      {result && !result.has_prior_job && (
        <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.3)}`, borderRadius:'12px',
          padding:'20px', fontSize:'14px' }}>
          <Icon name="check" /> No prior KaiLux AFS found for this client — this looks like a <strong>first-year
          entity</strong> (or the first AFS produced for them in KaiLux). There's nothing
          to reconcile against; the figures you entered will simply be used as the prior
          year comparatives.
        </div>
      )}

      {result && result.has_prior_job && result.items.length === 0 && (
        <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.3)}`, borderRadius:'12px',
          padding:'20px', fontSize:'14px' }}>
          <Icon name="check" /> All entered opening balances agree with the prior AFS (FY{result.prior_year}) —
          no differences found.
        </div>
      )}

      {result && result.has_prior_job && result.items.length > 0 && (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.bd}` }}>
            <div style={{ fontSize:'14px', fontWeight:'700' }}>
              {result.items.length} difference{result.items.length===1?'':'s'} found vs FY{result.prior_year} AFS
            </div>
            <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
              Review each, then accept it (you understand the cause) or flag it to ask the client
            </div>
          </div>
          {result.items.map((item, i) => (
            <div key={item.category} style={{ padding:'16px 20px',
              borderBottom: i < result.items.length-1 ? `1px solid ${T.bd}` : 'none' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'16px' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'14px', fontWeight:'700' }}>{item.category}</div>
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>
                    Prior AFS closing: R{item.prior_afs_closing.toLocaleString()} ·
                    {' '}Entered opening: R{item.entered_opening.toLocaleString()}
                  </div>
                  <div style={{ fontSize:'13px', color:T.lt, marginTop:'8px', lineHeight:'1.5' }}>
                    <Icon name="tip" /> {item.ai_note}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:'15px', fontWeight:'800',
                    color: item.difference > 0 ? T.gn : T.rd, fontFamily:"'Syne',sans-serif" }}>
                    {item.difference > 0 ? '+' : ''}R{item.difference.toLocaleString()}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:'8px', marginTop:'12px' }}>
                {actioned[item.category] ? (
                  <span style={{ fontSize:'12px', fontWeight:'700', padding:'6px 12px', borderRadius:'6px',
                    color: actioned[item.category]==='accept' ? T.gn : T.or,
                    background: actioned[item.category]==='accept' ? T.gnd : T.ord }}>
                    {actioned[item.category]==='accept' ? <><Icon name="check" /> Accepted</> : <><Icon name="flag" /> Flagged for client follow-up</>}
                  </span>
                ) : (
                  <>
                    <button onClick={() => handleAction(item, 'accept')}
                      style={{ padding:'7px 16px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.lt, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                      <Icon name="check" /> Accept — I understand this
                    </button>
                    <button onClick={() => handleAction(item, 'flag')}
                      style={{ padding:'7px 16px', borderRadius:'6px', border:`1px solid ${alpha(T.or, 0.25)}`,
                        background:T.ord, color:T.or, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                      <Icon name="flag" /> Flag — ask client
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
